import classNames from 'classnames';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { OperationContext, useQuery } from 'urql';
import { SiteView } from '~/components/site/SiteView';
import { ErrorMessage } from '~/components/ui/Error';
import { Loading } from '~/components/ui/Loading';
import {
  useListLocationDeploymentsQuery,
  useStockOnHandQuery,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import { SiteQuery } from '~/gql/graphql';
import { useTitle } from '~/hooks/index';

const Site_Query = graphql(`
  query Site($id: ID!) {
    integrations
    site(id: $id) {
      id
      status
      name
      type
      address
      image
      notes
      licensor {
        id
        name
      }
      defaultSource {
        id
        type
        name
      }
      attributes {
        ...AttributeFields
      }
      integrationAttributes {
        name
        value
      }
      images
      airbnbCalendar {
        id
        provider
        url
        syncedAt
      }
      airbnbDefaultCheckIn
      airbnbDefaultCheckOut
      homhero {
        id
        name
      }
    }
  }
`);

type ContextType = {
  site: NonNullable<SiteQuery['site']>;
  onSuccess: () => void;
  reloadStock: (opts?: Partial<OperationContext> | undefined) => void;
  reloadAssets: () => void;
};

export function useSite(): ContextType {
  return useOutletContext<ContextType>();
}

export function useSiteQuery(id: string) {
  return useQuery({
    // TODO change to cache-first and invalidate correctly where required
    requestPolicy: 'cache-and-network',
    query: Site_Query,
    variables: { id },
    pause: !id,
  });
}

export default function SiteRoute() {
  const outlet = useOutlet();
  const { siteId } = useParams();
  const location = useLocation();

  const [, reloadStock] = useStockOnHandQuery({
    requestPolicy: 'cache-and-network',
    variables: { location: `Site:${siteId}` },
  });

  const [, reloadAssetsQuery] = useListLocationDeploymentsQuery({
    variables: { id: `Site:${siteId}` },
    pause: !siteId,
  });

  const [{ fetching, data }] = useSiteQuery(siteId!);
  const navigate = useNavigate();

  useTitle(data?.site ? [data.site.name].join(' ') : undefined);

  const site = data?.site;

  const reloadAssets = () => {
    reloadAssetsQuery({ requestPolicy: 'network-only' });
  };

  // TODO FIXME
  // useEffect(() => {
  //   if (site && view !== site.type) {
  //     setView(
  //       PRESET_SITE_TYPES.findIndex((t) => t === site.type) > -1
  //         ? site.type ?? ''
  //         : 'Other'
  //     );
  //   }
  // }, [site, view, setView]);

  if (fetching) return <Loading />;
  if (!site) return <ErrorMessage message='Site not found' />;

  const handleClose = () => {
    navigate('..');
  };

  const isCalendarView = location.pathname.includes('/jobs/calendar');

  // Check if we're in a state where SiteView should take full width
  // This happens when no forms are being shown in the outlet
  const shouldSiteViewTakeFullWidth = !outlet;

  return (
    <div className='relative flex h-full'>
      <div
        className={classNames('flex-1', {
          // When no outlet, SiteView takes full width
          'w-full': shouldSiteViewTakeFullWidth,
          // When outlet exists, SiteView takes left side
          'xl:w-rsb xl:flex-none': !shouldSiteViewTakeFullWidth,
        })}
      >
        <SiteView siteId={site.id} onClose={handleClose} />
      </div>
      {outlet && (
        <div
          data-outlet
          className={classNames(
            'absolute z-50 h-full w-full xl:w-[414px]',
            !isCalendarView ? 'xl:relative' : ''
          )}
        >
          <Outlet context={{ site, job: false, reloadStock, reloadAssets }} />
        </div>
      )}
    </div>
  );
}
